<template>
    <div class="library">
        <left-nav
            :activeId="selectedPath ? selectedPath.path : null"
            @click="selectPath"
            :highlight-group="draggingOver"
            @dragover-group="leftNavDragOver"
            @dragleave-group="leftNavDragleave"
            @drop-group="leftNavDrop"
            v-if="!isDialog"
        ></left-nav>

        <div class="main" @mousedown="selectedPath = null">
            <h2>Form Library</h2>
            <div class="split">
                <search-bar :items="searchItems" @result-selected="openSearchItem" />
                <div class="button-group">
                    <submit-button :click="loadLibrary" title="Refresh the library">
                        <i class="far fa-sync-alt"></i>
                    </submit-button>
                    <button
                        v-if="!this.readOnly && user.hasRole('editor')"
                        class="button icon"
                        @click="() => openNewFolder()"
                    >
                        <i class="far fa-folder-open"></i>New Folder
                    </button>
                    <submit-button
                        v-if="!this.readOnly && user.hasRole('editor')"
                        class="button primary icon"
                        :click="openNewForm"
                    >
                        <i class="far fa-plus"></i>Build a Form
                    </submit-button>
                </div>
            </div>

            <div class="forms">
                <div class="path-bar">
                    <button
                        class="button primary parent-folder"
                        :disabled="currentPath === ''"
                        @click="openParentFolder"
                        title="Go up one folder"
                    >
                        <i class="far fa-level-up-alt"></i>
                    </button>
                    <div class="path-links">
                        <div class="link" v-for="(path, index) in currentPathArray" :key="index">
                            <i class="far fa-angle-right" v-if="index"></i>
                            <a
                                @click="openFolder(path)"
                                @dragover="itemDragOver($event, path)"
                                @dragleave="itemDragLeave($event)"
                                @drop="itemDrop($event, path)"
                                :class="{ 'dragging-over': path === draggingOver }"
                                >{{ displayName(path) || 'Library' }}</a
                            >
                        </div>
                    </div>
                </div>

                <div class="sort-bar">
                    <div class="sort-by name" @click="sort('name')">
                        Name
                        <i
                            class="far"
                            :class="{
                                hidden: sortBy !== 'name',
                                'fa-arrow-up': sortAsc,
                                'fa-arrow-down': !sortAsc,
                            }"
                        ></i>
                    </div>
                    <div class="sort-by modified" @click="sort('modified')">
                        Modified
                        <i
                            class="far"
                            :class="{
                                hidden: sortBy !== 'modified',
                                'fa-arrow-up': sortAsc,
                                'fa-arrow-down': !sortAsc,
                            }"
                        ></i>
                    </div>
                    <div class="sort-by modified-by" @click="sort('modified-by')">
                        Modified By
                        <i
                            class="far"
                            :class="{
                                hidden: sortBy !== 'modified-by',
                                'fa-arrow-up': sortAsc,
                                'fa-arrow-down': !sortAsc,
                            }"
                        ></i>
                    </div>
                </div>

                <div v-if="!currentItems.length" class="no-forms">
                    <div class="no-forms-rectangle" @click="openNewForm">
                        <form-thumbnail :showAdd="true" />
                        <div class="no-forms-link-text">Build a Form</div>
                        <div class="no-forms-description">There are no forms in here yet.</div>
                    </div>
                </div>

                <template v-for="(path, index) in sortedCurrentItems">
                    <list-item
                        :key="path.path"
                        :class="{
                            selected: selectedPath === path,
                            sorting: isSorting,
                            dragging: path === dragging,
                            disabled: readOnly && disableForms.includes(path.form_template_id),
                            'dragging-over': path.path === draggingOver,
                            'item-folder': !path.form_template_id,
                        }"
                        :draggable="user.hasRole('editor')"
                        :ref="path.path"
                        @contextmenu.native.prevent="showContextMenu"
                        @mousedown.native.stop="selectPath(path.path)"
                        @dblclick.native.stop="actions.find((action) => action.isDefault).callback()"
                        @dragstart.native="itemDragStart($event, path.path)"
                        @dragover.native="itemDragOver($event, path.path)"
                        @dragleave.native="itemDragLeave($event)"
                        @drop.native="itemDrop($event, path.path)"
                        @dragend.native="itemDragEnd($event)"
                    >
                        <div class="item-info" :class="path.form_template_id ? 'form' : 'folder'">
                            <template v-if="!!path.form_template_id">
                                <form-thumbnail :src="forms[path.form_template_id].data.thumbnail" />
                                <div class="form-details">
                                    <div class="form-title-section">
                                        <div class="form-title">
                                            {{ displayName(path.path) }}
                                        </div>
                                        <div class="item-label">
                                            <i class="far fa-file-alt"></i>
                                            {{ forms[path.form_template_id].data.pages.length }}
                                            Page{{ forms[path.form_template_id].data.pages.length > 1 ? 's' : '' }}
                                        </div>
                                        <div class="item-label" title="Last Updated">
                                            <i class="far fa-user-clock"></i>
                                            {{ formatDate(path.updated_at) }}
                                        </div>
                                    </div>
                                    <div class="form-actions">
                                        <template v-if="readOnly">
                                            <button class="primary" @click="selectForm(path)">Select</button>
                                        </template>
                                        <template v-else>
                                            <button
                                                v-if="user.hasRole('editor')"
                                                class="button icon"
                                                @click="editForm(path.form_template_id)"
                                            >
                                                <i class="far fa-edit"></i>
                                                Edit this
                                                {{ forms[path.form_template_id].is_draft ? 'Draft' : 'Form' }}
                                            </button>
                                            <button
                                                v-if="!forms[path.form_template_id].is_draft"
                                                class="button form-hover"
                                                @click="openSendFormDialog([forms[path.form_template_id]])"
                                            >
                                                Use this Form
                                                <i class="far fa-arrow-right"></i>
                                            </button>
                                        </template>
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <div class="folder-thumbnail">
                                    <img src="@/assets/images/folder.svg" class="folder-img" />
                                </div>
                                <div class="folder-details">
                                    <div class="folder-title">{{ displayName(path.path) }}</div>
                                </div>
                            </template>
                        </div>
                    </list-item>
                    <div v-if="index === currentFolders.length - 1" :key="'_' + index" class="folder-spacer"></div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
    const dateFormat = require('dateformat');
    import { mapGetters } from 'vuex';
    import { strictEncode } from '../../utils/strings';
    import { constants, dom } from '../../utils';
    import FormThumbnail from '../FormThumbnail';

    export default {
        name: 'LibraryMain',
        data() {
            return {
                dialogPath: '',
                currentPath: '',
                currentItems: [],
                sortBy: 'name',
                sortAsc: true,
                selectedPath: null,
                selectedForm: null,
                isSorting: false,
                dragging: null,
                draggingOver: null,
            };
        },
        components: {
            FormThumbnail,
        },
        props: {
            initialPath: {
                type: String,
                default: '',
            },
            isDialog: {
                type: Boolean,
                default: false,
            },
            readOnly: {
                type: Boolean,
                default: false,
            },
            disableForms: {
                type: Array,
                default: () => [],
            },
        },
        computed: {
            ...mapGetters({
                org: 'org/org',
                user: 'user/user',
                requiresOnboarding: 'user/requiresOnboarding',
                forms: 'library/forms',
                paths: 'library/paths',
                endOfPath: 'library/endOfPath',
                parentOfPath: 'library/parentOfPath',
                pathName: 'library/pathName',
            }),
            pinned() {
                return this.user.pinned_paths;
            },
            searchItems() {
                const items = [];
                for (let path in this.paths) {
                    const isForm = !!this.paths[path].form_template_id;
                    items.push({
                        text: decodeURIComponent(this.endOfPath(path)),
                        path,
                        icon: isForm ? 'far fa-file-alt' : 'far fa-folder-open',
                        searchable: isForm ? ['path'] : ['text'],
                    });
                }
                return items;
            },
            currentPathArray() {
                const arr = [];
                let i = -1;
                while ((i = this.currentPath.indexOf('/', i + 1)) >= 0) {
                    arr.push(this.currentPath.substr(0, i));
                }
                arr.push(this.currentPath);
                return arr;
            },
            actions() {
                if (!this.selectedPath) return [];

                if (this.readOnly) {
                    if (this.selectedForm) {
                        return [
                            {
                                name: 'Select',
                                isDefault: true,
                                callback: this.selectForm,
                            },
                        ];
                    } else {
                        return [
                            {
                                name: 'Open',
                                isDefault: true,
                                icon: 'far fa-folder-open',
                                callback: () => this.openFolder(),
                            },
                        ];
                    }
                }

                let actions = [];

                if (this.selectedForm) {
                    if (!this.selectedForm.is_draft) {
                        actions.push({
                            name: 'Use',
                            isDefault: true,
                            icon: 'far fa-paper-plane',
                            callback: () => this.openSendFormDialog(),
                        });
                    }

                    if (this.user.hasRole('editor')) {
                        actions.push({
                            name: 'Edit',
                            icon: 'far fa-pencil',
                            callback: () => this.editForm(),
                        });
                    }
                } else {
                    actions.push({
                        name: 'Open',
                        isDefault: true,
                        icon: 'far fa-folder-open',
                        callback: () => this.openFolder(),
                    });
                }

                if (this.isPinned(this.selectedPath)) {
                    actions.push({
                        name: 'Unpin',
                        icon: 'far fa-thumbtack',
                        callback: () => this.unpinPath(),
                    });
                } else {
                    actions.push({
                        name: 'Pin',
                        icon: 'far fa-thumbtack',
                        callback: () => this.pinPath(),
                    });
                }

                if (this.selectedForm) {
                    actions.push(
                        {
                            name: 'Download PDF',
                            icon: 'far fa-download',
                            callback: () =>
                                this.$store.dispatch('form/download', {
                                    formId: this.selectedForm.id,
                                    drafts: this.selectedForm.is_draft,
                                }),
                        },
                        {
                            name: 'Export Submissions CSV',
                            icon: 'far fa-file-spreadsheet',
                            callback: () =>
                                this.$store.dispatch('form/exportCsv', this.selectedForm.id),
                        },
                    );
                }

                if (this.user.hasRole('editor')) {
                    actions.push({
                        name: 'Rename',
                        icon: 'far fa-i-cursor',
                        callback: () => this.openRename(),
                    });

                    if (this.selectedForm) {
                        actions.push({
                            name: 'Duplicate',
                            icon: 'far fa-copy',
                            callback: () => this.duplicate(),
                        });
                    }

                    if (this.selectedForm && this.selectedForm.is_draft) {
                        actions.push({
                            name: 'Discard',
                            typeOverride: 'Draft',
                            icon: 'far fa-eraser',
                            callback: () => this.discardDraft(),
                        });
                    } else {
                        actions.push({
                            name: 'Delete',
                            icon: 'far fa-trash',
                            callback: () => {
                                this.selectedForm ? this.deleteForm() : this.deleteFolder();
                            },
                        });
                    }
                }

                return actions;
            },
            currentFolders() {
                return this.currentItems.filter((item) => {
                    return !item.form_template_id && this.paths[item.path];
                });
            },
            currentForms() {
                return this.currentItems.filter((item) => {
                    return item.form_template_id && this.paths[item.path];
                });
            },
            sortedCurrentItems() {
                return this.currentFolders.concat(this.currentForms);
            },
        },
        methods: {
            displayName(path) {
                path = this.pathName(path);
                if (path.length <= 160) return path;

                return path.substr(0, 157) + '...';
            },
            selectForm(path = this.selectedPath) {
                this.$emit('itemsSelected', [path]);

                if (this.isDialog) {
                    this.$store.dispatch('dialog/emit', {
                        name: 'itemsSelected',
                        payload: [path],
                    });
                }
            },
            async loadLibrary(setPath = null) {
                await this.$store.dispatch('library/load', this.readOnly);
                this.loadFolder(setPath === null ? this.currentPath : setPath);
            },
            sort(sortBy = null) {
                if (sortBy) {
                    this.sortAsc = this.sortBy === sortBy ? !this.sortAsc : true;
                    this.sortBy = sortBy;
                }
                this.isSorting = true;
                this.currentItems.sort((path1, path2) => {
                    let val1, val2;
                    if (this.sortBy === 'name') {
                        val1 = this.endOfPath(path1.path).toLowerCase();
                        val2 = this.endOfPath(path2.path).toLowerCase();
                    } else if (this.sortBy === 'modified') {
                        val1 = path1.updated_at;
                        val2 = path2.updated_at;
                    } else if (this.sortBy === 'modified-by') {
                        val1 = path1.updated_by_name;
                        val2 = path2.updated_by_name;
                    }
                    if (this.sortAsc) {
                        return val1 < val2 ? -1 : 1;
                    }
                    return val1 < val2 ? 1 : -1;
                });
                setTimeout(() => (this.isSorting = false));
            },
            async selectPath(path, replace = false) {
                if (!path || !this.paths[path]) return;

                if (this.paths[path].form_template_id) {
                    this.openFolder(this.parentOfPath(path), replace);
                    await this.$timeout();
                    this.selectedPath = this.paths[path];
                    this.selectedForm = this.forms[this.selectedPath.form_template_id];
                } else {
                    this.selectedPath = this.paths[path];
                    this.selectedForm = null;
                }
                dom.scrollIntoView(this.$refs[path][0].$el);
            },
            openItem(index) {
                const path = this.currentItems[index];
                if (path.form_template_id) {
                    this.selectPath(path.path);
                } else {
                    this.openFolder(path.path);
                }
            },
            openSearchItem(item) {
                const path = this.paths[item.path];
                if (path.form_template_id) {
                    this.openFolder(this.parentOfPath(path.path));
                    this.selectPath(path.path);
                } else {
                    this.openFolder(path.path);
                }
            },
            openFolder(path = this.selectedPath.path, replace = false) {
                const route = '/library' + (path ? '/folder' + path : '');
                if (this.isDialog) {
                    this.dialogPath = path;
                } else if (this.$route.path !== route || this.$route.query.path) {
                    if (replace) {
                        this.$router.replace(route);
                    } else {
                        this.$router.push(route);
                    }
                }
            },
            loadFolder(path) {
                // Ensure this path actually exists
                if (path && !this.paths[path]) {
                    let folderName = decodeURIComponent(this.endOfPath(path));
                    this.$store.dispatch('notification/set', {
                        type: 'warning',
                        message: `The folder "${folderName}" no longer exists`
                    });
                    this.openFolder('', true);
                    return;
                }

                this.currentPath = path;
                this.currentItems = [];
                this.selectedPath = null;
                this.selectedForm = null;
                const regex = new RegExp('^' + this.currentPath + '/[^/]+$');
                for (let path in this.paths) {
                    if (regex.test(path)) {
                        this.currentItems.push(this.paths[path]);
                    }
                }
                this.sort();
            },
            openParentFolder() {
                const path = this.currentPath.substr(0, this.currentPath.lastIndexOf('/'));
                this.openFolder(path);
            },
            async openNewFolder(startName = '') {
                let name = await this.$store.dispatch('dialog/set', {
                    type: 'prompt',
                    header: 'Create a New Folder',
                    text: startName,
                    body: 'Please provide a name for this folder:',
                    okText: 'Create',
                });

                if (name === false) return;

                this.createFolder(name);
            },
            async createFolder(newFolderName) {
                let res;
                try {
                    res = await this.$api.post('paths', {
                        folder: this.currentPath,
                        name: newFolderName,
                    });
                } catch (err) {
                    this.$store.dispatch('notification/set', {
                        type: 'error',
                        message: err.message,
                        timeout: 5000,
                    });
                    return;
                }

                await this.loadLibrary();
                this.selectPath(res.path);
            },
            async deleteFolder(path = this.selectedPath.path) {
                try {
                    await this.$api.delete('paths/' + encodeURIComponent(path));
                } catch (err) {
                    this.$store.dispatch('notification/set', {
                        message: err.message,
                        type: 'error',
                        timeout: 5000,
                    });
                    return;
                }
                this.loadLibrary();
            },
            async openRename(path = this.selectedPath, startName = this.displayName(path.path)) {
                let name = await this.$store.dispatch('dialog/set', {
                    type: 'prompt',
                    header: `Rename ${path.form_template_id ? 'Form' : 'Folder'}`,
                    text: startName,
                    body: 'New name:',
                    okText: 'Rename',
                });

                if (name === false) return;

                this.renamePath(path, name);
            },
            async renamePath(path, name) {
                let newPath = await this.executeRename(path.path, this.parentOfPath(path.path), name);

                if (!newPath) {
                    return this.openRename(path, name);
                }

                this.selectPath(newPath);
            },
            async executeRename(oldPath, folder, name) {
                let pathData;

                try {
                    pathData = await this.$api.post('paths/update', {
                        oldPath,
                        folder,
                        name,
                    });
                } catch (err) {
                    this.$store.dispatch('notification/set', {
                        message: err.message,
                        type: 'error',
                        timeout: 5000,
                    });
                    return false;
                }
                await this.loadLibrary();
                return pathData.path;
            },
            async openNewForm() {
                try {
                    await this.$api.get('forms/limit');
                    this.$store.commit('form/resetForms');
                    this.$store.commit('form/setPendingPath', this.currentPath);
                    this.$router.push('/builder');
                } catch (e) {
                    if (e.code !== 'LIM-FRM') throw e;
                }
            },
            editForm(formId = this.selectedForm.id) {
                this.$store.commit('form/setPendingPath', this.currentPath);
                this.$router.push(`/builder/${formId.substr(0, 8)}`);
            },
            async openSendFormDialog(forms = [this.selectedForm]) {
                let app = this;
                let minRole = this.org.settings.publicLinkMinRole;
                if (!this.user.hasRole(minRole)) {
                    launchOffice(false);
                    return;
                }

                this.$store.dispatch('dialog/set', {
                    type: 'info',
                    componentName: 'choose-package-type',
                    bodyComponentProps: {
                        forms,
                    },
                    dialogProps: {
                        showFooter: false,
                        showX: true,
                    },
                    listeners: {
                        select: launchOffice,
                    },
                });

                async function launchOffice(isPublic) {
                    app.$loading('Creating a form package', false);
                    let pkg = await app.$api.post('packages', {
                        formIds: forms.map(f => f.id),
                        isPublic,
                    });
                    app.$router.push(`/office/${isPublic ? 'post' : 'send'}/${pkg.id}`);
                }
            },
            async deleteForm(formId = this.selectedForm.id) {
                let confirm = await this.$store.dispatch('dialog/set', {
                    type: 'confirm',
                    header: 'Delete Form',
                    critical: true,
                    body: 'Are you sure you want to permanently delete this form?',
                });
                if (!confirm) return;
                await this.$api.delete(`forms/${formId}`);
                this.loadLibrary();
            },
            async discardDraft(formId = this.selectedForm.id) {
                let versions = await this.$api.get(`/forms/versions/${formId}`);
                let hasPublishedVersion = versions.some((version) => !version.is_draft && !version.is_archived);
                let body = 'Are you sure you want to discard this draft?';

                if (hasPublishedVersion) {
                    body += ' This will revert the form to the last published version.';
                }

                this.$store.dispatch('dialog/set', {
                    type: 'confirm',
                    header: 'Discard Draft',
                    critical: true,
                    body,
                    ok: async () => {
                        await this.$api.delete(`forms/${formId}/draft`);
                        this.loadLibrary();
                    },
                });
            },
            isPinned(path) {
                return path && this.user.pinned_paths.indexOf(path.path) >= 0;
            },
            itemDragStart(e, path) {
                this.dragging = path;
                e.dataTransfer.dropEffect = 'move';
                e.dataTransfer.effectAllowed = 'move';
            },
            itemDragOver(e, path) {
                e.preventDefault();
                if (path !== this.dragging && path !== this.draggingOver && (!path || !this.paths[path].form_template_id)) {
                    this.draggingOver = path;
                }
            },
            itemDragLeave(e) {
                this.draggingOver = null;
            },
            async itemDrop(e, path) {
                if (path && this.paths[path].form_template_id) return;
                if (this.dragging === path) return;

                e.preventDefault();
                const newPath = path + '/' + this.endOfPath(this.dragging);
                if (this.dragging === newPath) return;

                await this.executeRename(this.dragging, path, decodeURIComponent(this.endOfPath(this.dragging)));
                this.selectPath(path);
            },
            itemDragEnd(e) {
                this.draggingOver = null;
                this.dragging = null;
            },
            leftNavDragOver(groupId) {
                if (groupId !== this.draggingOver) {
                    this.draggingOver = groupId;
                }
            },
            pinPath(path = this.selectedPath) {
                this.$store.dispatch('library/pinPath', path.path);
            },
            unpinPath(path = this.selectedPath) {
                this.$store.dispatch('library/unpinPath', path.path);
            },
            leftNavDragleave(groupId) {
                this.draggingOver = null;
            },
            leftNavDrop(groupId) {
                if (groupId === 'pinned') {
                    this.pinPath(this.paths[this.dragging]);
                }
            },
            formatDate(date, format = constants.LONG_DATE_FORMAT) {
                return dateFormat(date, format);
            },
            async showContextMenu(e) {
                await this.$timeout();
                this.$store.commit('context/set', {
                    items: this.actions,
                    x: e.clientX,
                    y: e.clientY,
                });
            },
            async initLibrary() {
                let setPath = null;
                if (this.isDialog) {
                    this.$store.commit('dialog/loading', true);
                    this.dialogPath = this.initialPath;
                    setPath = this.dialogPath;
                } else {
                    // Both loading lines necessary to prevent loading screen flickering when returning from office
                    this.$loading(true);
                    setTimeout(() => this.$loading(true));
                    setPath = this.getPathFromRoute(this.$route) || null;
                }

                await this.loadLibrary(setPath);

                if (this.$route.query.path) {
                    this.selectPath(this.$route.query.path, true);
                }

                if (this.isDialog) {
                    this.$store.commit('dialog/loading', false);
                } else {
                    this.$loading(false);
                }
            },
            async duplicate(formId = this.selectedForm.id) {
                try {
                    let res = await this.$api.post(`forms/${formId}/copy`);
                    await this.loadLibrary();
                    this.selectPath(res.path);
                } catch (e) {
                    if (e.code !== 'LIM-FRM') throw e;
                }
            },
            getPathFromRoute(route) {
                let path = '';
                if (route.params.pathMatch) {
                    path = route.path.substr(16);
                    if (path) path = '/' + strictEncode(path, true);
                } else if (route.query.path) {
                    path = this.parentOfPath(route.query.path);
                }
                return path;
            },
        },
        mounted() {
            if (this.requiresOnboarding) return;
            this.initLibrary();
        },
        watch: {
            $route(to, from) {
                let path = this.getPathFromRoute(to);
                this.loadFolder(path);
            },
            dialogPath(path) {
                this.loadFolder(path);
            },
            requiresOnboarding(newVal) {
                if (!newVal) this.initLibrary();
            },
        },
    };
</script>
<style lang="scss" scoped>
    .library {
        display: flex;
        height: 100%;
    }

    .main {
        height: 100%;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        padding: 0 2em;
        text-align: left;
    }

    .search-wrapper {
        display: flex;
        flex-grow: 1;
        margin-right: 50px;
    }

    .split {
        @include flex-split;
    }

    .forms {
        $thumbnail_size: 30px;
        $thumbnail_margin: 20px;
        margin-left: -10px;
        margin-right: -10px;
        margin-top: 1em;

        .item {
            width: calc(33.33% - 20px);
            margin: 10px;
            display: inline-flex;
            padding: 17px 25px;
            border: 1px solid $color-border;
            border-radius: $size-border-radius-small;
            height: 215px !important;

            &.disabled {
                pointer-events: none;
                background-color: $color-disabled-bg;
                color: $color-disabled-text;

                button {
                    @extend button.disabled;
                }
            }

            &:hover {
                border-color: darken($color-border, 30);
                background-color: unset;

                .form-actions {
                    button.form-hover {
                        background-color: $color-main;
                        border-color: $color-main;
                        color: #fff;

                        &:hover {
                            background-color: lighten($color-main, 5);
                        }
                    }
                }
            }

            &.sorting {
                transition: none;
            }

            &.dragging {
                cursor: grabbing;
            }

            &.selected {
                background-color: $color-select;
            }

            .folder-details {
                width: calc(100% - 75px);
            }

            .form-details {
                width: calc(100% - 160px);
                height: 180px;
                padding-top: 10px;
                vertical-align: top;
            }

            .form-actions {
                button:first-child {
                    margin-right: 10px;
                }

                button.form-hover {
                    color: $color-main;
                    font-weight: 700;

                    i {
                        margin-left: 5px;
                        font-size: 0.7rem;
                    }
                }
            }

            .form-title-section {
                height: 125px;
            }

            .item-label {
                font-size: $font-size-smaller;
                color: $color-label;
                display: inline-block;
                margin: 0 9px 0 1px;
            }

            .form-title {
                font-size: $font-size-largest;
                margin-bottom: 10px;
                line-height: 140%;
                font-weight: 700;
            }

            .property-title {
                font-size: 0.7rem;
                text-transform: uppercase;
                opacity: 0.6;
            }

            &.item-folder {
                height: 90px !important;
                padding-left: 18px;

                .folder-title {
                    font-size: $font-size-largest;
                    line-height: 1.2em;
                    font-weight: 700;
                    margin-bottom: 5px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .folder-info {
                    text-transform: uppercase;
                    opacity: 0.8;
                    margin-top: 5px;
                }

                .folder-thumbnail {
                    height: 40px;
                    width: 60px;
                    min-width: 60px;
                    margin-right: 15px;
                    display: flex;

                    .folder-img {
                        height: 100%;
                    }
                }
            }

            @media only screen and (max-width: 1800px) {
                width: calc(50% - 20px);
            }

            @media only screen and (max-width: 1350px) {
                width: calc(100% - 20px);
                height: auto !important;

                .form-details {
                    height: auto;
                }
            }
        }

        .dragging-over {
            @include dragging-over;
        }

        .item-info {
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            height: auto;
            width: 100%;
        }

        .path-bar {
            margin-bottom: 1.5em;
            margin-left: 10px;
            margin-right: 10px;
            display: flex;
            flex-direction: row;
            align-items: flex-start;

            .parent-folder {
                font-size: 80%;
                width: 40px;
                margin-right: 1em;
                padding: 3px;
                border-radius: 3px;
                flex-shrink: 0;
            }

            .path-links {
                user-select: none;

                .link {
                    display: inline-block;
                    white-space: nowrap;

                    i {
                        font-size: 0.8em;
                        margin: 0 0.5em;
                    }

                    a {
                        padding: 4px 8px;
                    }
                }
            }
        }

        .sort-bar {
            display: none;
            flex-direction: row;
            margin-bottom: 5px;
            padding: 0 10px 0 $thumbnail_size + $thumbnail_margin + 10px;

            .sort-by {
                font-weight: $bold;
                cursor: pointer;
                user-select: none;

                i {
                    margin-left: 0.4em;
                    font-size: 0.9em;
                }
            }

            .name {
                flex-grow: 1;
            }
        }

        .form-thumbnail {
            margin-right: 20px;
        }

        .sort-bar,
        .item {
            user-select: none;

            .modified {
                width: 11em;
                margin-bottom: 5px;
            }

            .modified-by {
                width: 11em;
            }
        }

        .no-forms {
            text-align: center;
            margin-top: 120px;
            user-select: none;
            display: flex;
            justify-content: center;

            ::v-deep .no-forms-rectangle {
                border-radius: 45%;
                background-color: lighten($color-disabled-bg, 2);
                opacity: 1;
                z-index: 2;
                padding: 45px 70px;
                display: flex;
                flex-direction: column;
                align-items: center;
                flex-wrap: nowrap;
                cursor: pointer;
                transition: all 0.3s;
                z-index: 5;

                .form-thumbnail {
                    margin-left: 10px;
                }

                .no-forms-link-text {
                    margin-top: 20px;
                    font-size: $font-size-largest;
                    font-weight: bold;
                    transition: all 0.3s;
                }

                .no-forms-description {
                    margin-top: 15px;
                    color: $color-label;
                }

                &:hover {
                    .form-thumbnail-element {
                        background-color: $color-main;
                        opacity: 0.2;
                    }

                    .form-thumbnail-add-icon {
                        color: $color-white;
                        background-color: $color-main;
                        border-color: $color-main;
                    }

                    .starting-circle-2 {
                        background-color: $color-main;
                        opacity: 0.2;
                    }
                }
            }
        }
    }

    .split {
        @include flex-split;
    }
</style>
